<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_CONFIG', 1) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6 dark:text-gray-300"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_CONFIG', 2) }}
                        </li>
                    </ol>
                </nav>
            </div>
            <!-- breadcrumb end -->

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-visible"
            >
                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th class="px-4 py-3">#</th>
                                    <th class="px-4 py-3">KEY</th>
                                    <th class="px-4 py-3">VALUE</th>
                                    <th class="px-4 py-3">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(row, rowIndex) in commons.data">
                                    <tr
                                        :class="[
                                            'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200',
                                        ]"
                                    >
                                        <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td>
                                        <td class="px-4 py-3">{{ row.key }}</td>
                                        <td class="px-4 py-3">{{ userConfigValue(row) }}</td>
                                        <td class="px-4 py-3">
                                            <t-button
                                                variant="outline"
                                                @click="edit(row.id)"
                                            >
                                                Edit
                                            </t-button>
                                        </td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!commons.data.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="4"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        v-if="commons.data.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="disabled"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>

            <RightSidebar
                :is-open="rightSidebar"
                :component-name="rightSidebarComponentName"
                :edit-data="editData"
                @closeRightSidebar="toggleRightSidebar"
            >
                <template #heading>
                    <!-- Title -->
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div class="flex items-center gap-3 min-w-0 flex-1">
                            <h2 class="text-lg font-semibold text-gray-800">
                                <h2 class="text-xl font-semibold text-gray-800 dark:text-white">
                                    Configuration Information
                                </h2>
                            </h2>
                        </div>
                        <t-button
                            variant="text"
                            class="hover:text-red-500"
                            @click="rightSidebar = false"
                        >
                            ✕ Close
                        </t-button>
                    </div>
                </template>
            </RightSidebar>
        </div>
    </AppLayout>
</template>

<script>
    import AppLayout from '../../Layouts/AppLayout.vue';
    import RightSidebar from '../../Components/RightSidebar.vue';
    // import CreateIcon from '../../Components/Icons/CreateIcon.vue';

    export default {
        name: 'CommonsIndex',

        components: {
            AppLayout,
            RightSidebar,
            // CreateIcon,
        },

        props: {
            commons: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                rightSidebar: false,
                rightSidebarComponentName: 'CreateConfig',
                editData: null,
                currentPage: null,
                limit: 5,
                perPage: this.commons.per_page,
                totalRows: this.commons.total,
            };
        },

        computed: {
            disabled() {
                return !this.commons.data.length;
            },
        },

        watch: {
            rightSidebar(newVal) {
                if (newVal === false) {
                    this.editData = null;
                }
            },

            commons: {
                deep: true,
                handler() {
                    if (this.editData) {
                        this.editData = this.commons.data.find((common) => common.id === this.editData.id);
                    }
                },
            },
        },

        created() {
            this.currentPage = this.commons.current_page;
        },

        methods: {
            toggleRightSidebar() {
                this.rightSidebar = !this.rightSidebar;
            },

            edit(commonId) {
                const common = this.commons.data.find((item) => item.id === commonId);
                this.editData = common;
                this.toggleRightSidebar();
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('commons.index', {
                        page: newPage,
                    }),
                );
            },
        },
    };
</script>
